// LayoutEditorModalLayer.jsx

import React, { useMemo } from 'react';

// Utilities
import { getNodeBlockKey } from './pageEditorLayoutUtils';

// Components
import DraftViewBlockPickerModal from './DraftViewBlockPickerModal';
import FieldSelectorModal from './FieldSelectorModal';
import LayoutNodePickerModal from './LayoutNodePickerModal';
import { normalizePageNodePresentation } from './LayoutNodePresentation';
import LayoutPageViewTypeModal from './LayoutPageViewTypeModal';
import LayoutRichTextBlockModal from './LayoutRichTextBlockModal';
import LayoutRollupPickerModal from './LayoutRollupPickerModal';
import PageItemModal from './PageItemModal';
import AddViewCardModal from 'dashboard/modals/AddViewCardModal';
import ViewModal from 'views/modal/ViewModal';

/**
 * LayoutEditorModalLayer
 *
 * Renders the modal and picker stack used by LayoutEditor while keeping modal composition out of the main editor shell.
 *
 * @param {boolean} [allowNodeBlocks=true] - Whether node block picking is enabled.
 * @param {object} blocksByKey - Current block lookup map.
 * @param {array} fieldBlockDraftFieldKeys - Draft field keys for the field picker modal.
 * @param {boolean} fieldBlockModalOpen - Whether the field picker modal is open.
 * @param {array} fieldSelectorAvailableFields - Available fields for the field picker.
 * @param {function} getResolvedBlockValue - Resolves block overrides for a given block key.
 * @param {function} getResolvedRichTextBlockValue - Resolves rich-text block values for a given block key.
 * @param {function} getType - Node type lookup function.
 * @param {function} handleAssignFieldBlock - Persists a field block selection.
 * @param {function} handleAssignNodeBlock - Persists a node block selection.
 * @param {function} handleAssignRollupBlock - Persists a rollup block selection.
 * @param {function} handleAssignViewBlock - Persists a view block selection.
 * @param {function} handleCloseFieldBlockModal - Closes the field picker modal.
 * @param {function} handleCloseNodeBlockModal - Closes the node picker modal.
 * @param {function} handleClosePagePreviewItem - Closes the shared page preview item modal.
 * @param {function} handleClosePageViewTypeModal - Closes the page related-type modal.
 * @param {function} handleCloseRichTextBlockModal - Closes the rich-text block modal.
 * @param {function} handleCloseRollupBlockModal - Closes the rollup picker modal.
 * @param {function} handleCloseViewBlockModal - Closes the view definition modal.
 * @param {function} handleFieldBlockDraftFieldKeysChange - Updates the field picker draft selection.
 * @param {function} handleRemoveBlockFromSlot - Removes a block from a slot.
 * @param {function} handleSaveRichTextBlock - Persists rich-text content.
 * @param {function} handleSelectPageViewType - Commits the selected page related type.
 * @param {object} layout - Current layout tree.
 * @param {object|null} nodeByKey - Preview node lookup map.
 * @param {boolean} nodeBlockModalOpen - Whether the node picker is open.
 * @param {string} nodeBlockSlotKey - Active node block slot key.
 * @param {object|null} pagePreviewSelectedItem - Current preview item-modal selection.
 * @param {function|null} [pageViewGetAllTypeDefs=null] - Optional type catalog for the page related-type picker.
 * @param {string} pageViewHostTypeKey - Layout host type key for the page type picker.
 * @param {string} pageViewTypeModalNoun - Lowercase noun for the page type picker.
 * @param {boolean} pageViewTypeModalOpen - Whether the page type picker is open.
 * @param {string} pageViewTypeModalTitle - Title for the page type picker.
 * @param {string} resolvedPageViewDraftViewType - Pending page-safe view type.
 * @param {array|null} resolvedViewBlockAllowedTypeKeys - Allowed view type keys for the current editor surface.
 * @param {string} resolvedViewBlockTypeKey - Resolved type key for the current view block flow.
 * @param {boolean} richTextBlockModalOpen - Whether the rich-text block modal is open.
 * @param {string} richTextBlockSlotKey - Active rich-text block slot key.
 * @param {string} richTextDocumentLabel - Document label shown in the rich-text modal.
 * @param {boolean} rollupBlockModalOpen - Whether the rollup picker is open.
 * @param {object|null} [settingsModal=null] - Optional externally provided settings modal.
 * @param {boolean} useLocalViewDefinitions - Whether the current editor uses local view definitions.
 * @param {string} viewBlockModalCreatedFrom - Created-from label for the view modal.
 * @param {string} viewBlockModalEmptyStateSubtitle - Empty-state subtitle for the shared AddViewCardModal.
 * @param {boolean} viewBlockModalOpen - Whether the view definition modal is open.
 * @param {string} viewBlockModalTitle - Title for the AddViewCardModal flow.
 * @param {string} viewBlockSlotKey - Active view block slot key.
 * @param {array|null} viewBlockDraftViews - Optional draft view definitions for Studio-style pickers.
 * @param {string} viewBlockDraftDatabaseLabel - Database label used in the draft-view picker empty state.
 * @param {string} viewBlockRelatedTypeKey - Related type key stored on draft view block assignments.
 * @param {string} baseKey - Active base key.
 * @returns {ReactNode} The rendered modal layer.
 */
const LayoutEditorModalLayer = ({
	allowNodeBlocks = true,
	blocksByKey,
	dashboardKey = null,
	dashboardViewAuthoringEnabled = false,
	dashboardViewModalMode = 'GLOBAL',
	fieldBlockDraftFieldKeys,
	fieldBlockModalOpen,
	fieldSelectorAvailableFields,
	fieldSelectorLockedFieldKeys = [],
	getResolvedBlockValue,
	getResolvedRichTextBlockValue,
	getType,
	handleAssignFieldBlock,
	handleAssignNodeBlock,
	handleAssignRollupBlock,
	handleAssignViewBlock,
	handleCloseFieldBlockModal,
	handleCloseNodeBlockModal,
	handleClosePagePreviewItem,
	handleClosePageViewTypeModal,
	handleCloseRichTextBlockModal,
	handleCloseRollupBlockModal,
	handleCloseViewBlockModal,
	handleFieldBlockDraftFieldKeysChange,
	handleRemoveBlockFromSlot,
	handleSaveRichTextBlock,
	handleSelectPageViewType,
	layout,
	nodeByKey,
	nodeBlockModalOpen,
	nodeBlockSlotKey,
	pagePreviewSelectedItem,
	pageViewGetAllTypeDefs = null,
	pageViewHostTypeKey,
	pageViewTypeModalNoun,
	pageViewTypeModalOpen,
	pageViewTypeModalTitle,
	resolvedPageViewDraftTypeKey = '',
	resolvedPageViewDraftViewType,
	resolvedViewBlockAllowedTypeKeys,
	resolvedViewBlockTypeKey,
	richTextBlockModalOpen,
	richTextBlockSlotKey,
	richTextDocumentLabel,
	rollupBlockModalOpen,
	settingsModal = null,
	useLocalViewDefinitions,
	viewBlockModalCreatedFrom,
	viewBlockModalEmptyStateSubtitle,
	viewBlockModalOpen,
	viewBlockModalTitle,
	viewBlockSlotKey,
	viewBlockDraftViews = null,
	viewBlockDraftDatabaseLabel = 'this collection',
	viewBlockExistingViewIds = null,
	viewBlockModalIntent = 'saved',
	viewBlockRelatedTypeKey = '',
	baseKey,
}) => {
	const useDraftViewBlockPicker = Array.isArray(viewBlockDraftViews);
	const shouldOpenDashboardCreateViewModal = dashboardViewAuthoringEnabled
		&& viewBlockModalIntent === 'create'
		&& Boolean(String(resolvedPageViewDraftTypeKey || '').trim())
		&& viewBlockModalOpen;
	const resolvedExistingViewIds = Array.isArray(viewBlockExistingViewIds)
		? viewBlockExistingViewIds
		: [];

	// ----- MEMOS ----- //

	const viewBlockMode = useMemo(() => {
		const slotNode = layout.nodes[viewBlockSlotKey] || null;
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;
		const resolvedBlock = getResolvedBlockValue(blockKey, block);
		return resolvedBlock?.view && typeof resolvedBlock.view === 'object' ? 'edit' : 'create';
	}, [blocksByKey, getResolvedBlockValue, layout.nodes, viewBlockSlotKey]);

	const resolvedLocalView = useMemo(() => {
		const slotNode = layout.nodes[viewBlockSlotKey] || null;
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;
		const resolvedBlock = getResolvedBlockValue(blockKey, block);

		return resolvedBlock?.view && typeof resolvedBlock.view === 'object'
			? resolvedBlock.view
			: null;
	}, [blocksByKey, getResolvedBlockValue, layout.nodes, viewBlockSlotKey]);

	const initialNodeBlockPresentation = useMemo(() => {
		const slotNode = layout.nodes[nodeBlockSlotKey] || null;
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;
		const resolvedBlock = getResolvedBlockValue(blockKey, block);

		return normalizePageNodePresentation(resolvedBlock?.presentation);
	}, [blocksByKey, getResolvedBlockValue, layout.nodes, nodeBlockSlotKey]);

	const initialRichTextValue = useMemo(() => {
		const slotNode = layout.nodes[richTextBlockSlotKey] || null;
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;
		return getResolvedRichTextBlockValue(blockKey, block);
	}, [blocksByKey, getResolvedRichTextBlockValue, layout.nodes, richTextBlockSlotKey]);

	// ----- RENDER ----- //

	return (
		<>
			{settingsModal}

			{useLocalViewDefinitions ? (
				<ViewModal
					contextHint="This view shows related records attached to each page item. Filters and visible fields apply to that related set only."
					createdFrom={viewBlockModalCreatedFrom}
					initialViewType={resolvedPageViewDraftViewType || 'LIST'}
					isOpen={viewBlockModalOpen}
					mode={viewBlockMode}
					setOpen={(nextOpen) => {
						if (!nextOpen) {
							handleCloseViewBlockModal();
						}
					}}
					typeKey={resolvedViewBlockTypeKey}
					view={resolvedLocalView}
					onDeleteDefinition={async () => {
						await handleRemoveBlockFromSlot(viewBlockSlotKey);
						handleCloseViewBlockModal();
					}}
					onSaveDefinition={async (nextView) => {
						await handleAssignViewBlock({
							typeKey: resolvedViewBlockTypeKey,
							view: nextView,
						});
					}}
				/>
			) : useDraftViewBlockPicker ? (
				<DraftViewBlockPickerModal
					databaseLabel={viewBlockDraftDatabaseLabel}
					draftViews={viewBlockDraftViews}
					isOpen={viewBlockModalOpen}
					relatedTypeKey={viewBlockRelatedTypeKey}
					title={viewBlockModalTitle}
					onClose={handleCloseViewBlockModal}
					onSelect={handleAssignViewBlock}
				/>
			) : shouldOpenDashboardCreateViewModal ? (
				<ViewModal
					createdFrom={viewBlockModalCreatedFrom}
					dashboardKey={dashboardKey}
					initialViewType={resolvedPageViewDraftViewType || 'LIST'}
					isOpen={viewBlockModalOpen}
					mode="create"
					setOpen={(nextOpen) => {
						if (!nextOpen) {
							handleCloseViewBlockModal();
						}
					}}
					typeKey={resolvedPageViewDraftTypeKey}
					baseKey={baseKey}
					onSave={async (createdView) => {
						const createdViewId = String(createdView?.key || '').trim();
						if (!createdViewId) return;

						await handleAssignViewBlock({
							relatedTypeKey: resolvedPageViewDraftTypeKey,
							titleOverride: createdView?.title || null,
							viewId: createdViewId,
						});
					}}
				/>
			) : (
				<AddViewCardModal
					allowedTypeKeys={resolvedViewBlockAllowedTypeKeys}
					createdFrom={viewBlockModalCreatedFrom}
					dashboardKey={dashboardKey}
					existingEmptyStateSubtitle={viewBlockModalEmptyStateSubtitle}
					existingViewIds={resolvedExistingViewIds}
					isOpen={viewBlockModalOpen}
					mode={dashboardViewModalMode}
					title={viewBlockModalTitle}
					onAdd={handleAssignViewBlock}
					onClose={handleCloseViewBlockModal}
				/>
			)}

			<LayoutPageViewTypeModal
				getAllTypeDefs={pageViewGetAllTypeDefs}
				hostTypeKey={dashboardViewAuthoringEnabled ? '' : pageViewHostTypeKey}
				isOpen={pageViewTypeModalOpen}
				title={dashboardViewAuthoringEnabled ? 'Add new view' : pageViewTypeModalTitle}
				viewNoun={dashboardViewAuthoringEnabled ? 'view' : pageViewTypeModalNoun}
				onClose={handleClosePageViewTypeModal}
				onSelect={handleSelectPageViewType}
			/>

			<FieldSelectorModal
				availableFields={fieldSelectorAvailableFields}
				isOpen={fieldBlockModalOpen}
				lockedFieldKeys={fieldSelectorLockedFieldKeys}
				selectedFieldKeys={fieldBlockDraftFieldKeys}
				onClose={handleCloseFieldBlockModal}
				onSave={handleAssignFieldBlock}
				onSelectedFieldKeysChange={handleFieldBlockDraftFieldKeysChange}
			/>

			{allowNodeBlocks ? (
				<LayoutNodePickerModal
					initialPresentation={initialNodeBlockPresentation}
					isOpen={nodeBlockModalOpen}
					baseKey={baseKey}
					onClose={handleCloseNodeBlockModal}
					onSelect={handleAssignNodeBlock}
				/>
			) : null}

			<LayoutRichTextBlockModal
				documentLabel={richTextDocumentLabel}
				initialValue={initialRichTextValue}
				isOpen={richTextBlockModalOpen}
				onClose={handleCloseRichTextBlockModal}
				onSave={handleSaveRichTextBlock}
			/>

			<LayoutRollupPickerModal
				isOpen={rollupBlockModalOpen}
				onClose={handleCloseRollupBlockModal}
				onSelect={handleAssignRollupBlock}
			/>

			<PageItemModal
				getTypeOverride={getType}
				isOpen={Boolean(pagePreviewSelectedItem?.node)}
				node={pagePreviewSelectedItem?.node || null}
				nodesByKey={nodeByKey}
				typeDef={pagePreviewSelectedItem?.typeDef || null}
				view={pagePreviewSelectedItem?.view || null}
				onClose={handleClosePagePreviewItem}
			/>
		</>
	);
};

// Dev Tools
LayoutEditorModalLayer.displayName = 'LayoutEditorModalLayer';

export default LayoutEditorModalLayer;
